import type { AssistantExecutionContext, AssistantExecutionResult } from './index';

const sanitizePathSegment = (value: unknown) =>
  String(value ?? '')
    .replace(/[<>:"/\\|?*\x00-\x1F]+/g, '')
    .trim();

const normalizeWindowsPath = (value: unknown) =>
  String(value ?? '')
    .replace(/\//g, '\\')
    .replace(/\\+/g, '\\')
    .trim();

const ensureSafeAbsolutePath = (value: unknown) => {
  const normalized = normalizeWindowsPath(value);
  if (!normalized) {
    throw new Error('A valid path is required.');
  }

  if (normalized.includes('..')) {
    throw new Error('Parent-directory segments are not allowed in assistant paths.');
  }

  if (!/^[A-Za-z]:\\/.test(normalized) && normalized !== 'C:\\') {
    throw new Error('Assistant paths must be absolute Windows-style paths.');
  }

  return normalized;
};

const joinWindowsPath = (basePath: string, segment: string) => {
  const normalizedBase = ensureSafeAbsolutePath(basePath);
  const safeSegment = sanitizePathSegment(segment);
  if (!safeSegment) {
    throw new Error('A valid note name is required.');
  }

  return normalizedBase.endsWith('\\')
    ? `${normalizedBase}${safeSegment}`
    : `${normalizedBase}\\${safeSegment}`;
};

const ensureTxtExtension = (value: string) =>
  value.toLowerCase().endsWith('.txt') ? value : `${value}.txt`;

const toNotepadTitle = (path?: string) => {
  const fileName = path?.split('\\').pop();
  return fileName ? `Notepad - ${fileName}` : 'Notepad';
};

export async function executeNotepadTool(
  actionId: string,
  args: Record<string, unknown>,
  ctx: AssistantExecutionContext,
): Promise<AssistantExecutionResult> {
  switch (actionId) {
    case 'open_note': {
      if (typeof args.path === 'string' && args.path.trim()) {
        const targetPath = ensureSafeAbsolutePath(args.path);
        const content = (await ctx.readFile(targetPath)) ?? '';
        ctx.openApp('notepad', toNotepadTitle(targetPath), targetPath, content);
        return {
          message: `Opened ${targetPath} in Notepad.`,
          shouldClose: true,
        };
      }

      if (typeof args.title === 'string' && args.title.trim()) {
        const documentsPath = await ctx.resolveDocumentsPath();
        const notesDir = `${documentsPath}\\Notes`;
        const fileName = ensureTxtExtension(sanitizePathSegment(args.title));
        const targetPath = joinWindowsPath(notesDir, fileName);
        const content = typeof args.content === 'string' ? args.content : '';

        await ctx.ensureDir(notesDir);
        const created = await ctx.createFile(targetPath, content);
        if (!created) {
          throw new Error(`Could not create the note at ${targetPath}.`);
        }

        ctx.openApp('notepad', toNotepadTitle(targetPath), targetPath, content);
        return {
          message: `Opened note ${targetPath}.`,
          shouldClose: true,
        };
      }

      const content = typeof args.content === 'string' ? args.content : '';
      ctx.openApp('notepad', 'Notepad', undefined, content);
      return {
        message: 'Opened a new Notepad window.',
        shouldClose: true,
      };
    }

    case 'save_note': {
      const targetPath = ensureSafeAbsolutePath(args.path);
      const content = typeof args.content === 'string' ? args.content : '';
      const parentPath = targetPath.slice(0, targetPath.lastIndexOf('\\')) || 'C:\\';

      await ctx.ensureDir(parentPath);
      const saved = await ctx.writeFile(targetPath, content);
      if (!saved) {
        throw new Error(`Could not save the note at ${targetPath}.`);
      }

      ctx.openApp('notepad', toNotepadTitle(targetPath), targetPath, content);
      return {
        message: `Saved note to ${targetPath}.`,
        shouldClose: true,
      };
    }

    default:
      throw new Error(`Unsupported Notepad action: ${actionId}.`);
  }
}
